import React, { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import posthog from 'posthog-js';
import log from 'loglevel';
import { IS_DEVELOPMENT } from 'api-config.mjs';
import { getSessionFromStorage, SessionContext } from 'components/Session';
import { clearSessionCookie, setSessionCookie } from 'components/Browser';
import { LogRocketLazyLoader } from "components/LogRocketLazyLoader";
import { OpenReplayLazyLoader } from "components/OpenReplayLazyLoader";
// Owns the support-chat conversation and its WebSocket. Mounted here, above
// the pages, so a page change (which remounts Layout and the widget) does not
// clear an in-progress chat. The widget itself is loaded lazily by Layout.
import { SupportChatProvider } from "components/support-chat/SupportChatProvider";
// import { loadAdCritterScripts, parseAdvertisingQueryParams } from "components/AdCritter";
// import TawkMessengerReact from '@tawk.to/tawk-messenger-react';

const queryClient = new QueryClient();

// Email to identify a logged-in user by, for any sink keyed on it (PostHog below,
// and getGtagUserId's hash further down). Google logins never populate userInfo:
// useGoogleLogin (@react-oauth/google) resolves a TokenResponse, which has no
// profileObj (that field belonged to the deprecated react-google-login), so
// `userInfo: response.profileObj` is undefined. Those users are still fully
// logged in and can pay — without this fallback they'd stay unidentified and
// their conversions couldn't be attributed. The server session carries the
// email for every login type.
const getIdentifyEmail = (session) => {
  const { email } = session.userInfo || {};
  const serverUser = session.session?.user || {};
  return email || serverUser.email;
};

// Identify a logged-in user to PostHog. No-op in development or when the session
// is not logged in. Shared by setSessionContext (on login) and the mount effect
// (for an already-seeded session) so the identify payload lives in one place.
// Docs: https://posthog.com/docs/getting-started/identify-users
const identifyPostHogUser = (session) => {
  if (IS_DEVELOPMENT) return;
  if (!session?.isLoggedIn) return;
  const identifyEmail = getIdentifyEmail(session);
  if (!identifyEmail) return;
  const { name, givenName, familyName } = session.userInfo || {};
  const serverUser = session.session?.user || {};
  const displayName =
    name ||
    [givenName, familyName].filter(Boolean).join(" ") ||
    serverUser.name ||
    undefined;
  posthog.identify(identifyEmail, {
    email: identifyEmail,
    ...(displayName && { name: displayName }),
  });
};

// The ID GA4's user_id gets set to. GA4's policy forbids PII in user_id, so the
// email itself can never be used directly here (unlike PostHog's distinct_id
// above, which PostHog allows keying on email).
//
// Until the backend returns a stable internal ID on session.user (see the /login
// response shape noted in components/Session.js), this hashes the identify email
// as a deterministic placeholder: the same person always hashes to the same
// value (so GA4 can still link their sessions across devices/cookie resets), but
// the hash can't be reversed back to an email. When the backend adds a real
// internal ID, swap this function's body only — every caller stays the same.
const getGtagUserId = async (session) => {
  const email = getIdentifyEmail(session);
  if (!email) return undefined;
  // crypto.subtle requires a secure context (HTTPS, or the localhost exception) —
  // true for every real deployment (alpha/prod are HTTPS) and for local dev/CI
  // (localhost), but guard anyway so a browser/environment without it degrades to
  // "no user_id this session" rather than an unhandled rejection.
  if (typeof window === "undefined" || !window.crypto?.subtle) return undefined;
  const normalized = email.trim().toLowerCase();
  const digest = await window.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(normalized),
  );
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

// Sets GA4's user_id for a logged-in visitor so Google Ads attribution survives a
// cookie reset or a purchase made from a different device than the ad click (GA4
// otherwise re-buckets that conversion under Direct — see the getGtagUserId
// comment for why this can't just be the email). NOT CONFIRMED to fix any specific
// prior attribution miss we've seen -- that would require knowing why that
// session failed to link, which we don't. This closes the cookie-reset/
// cross-device gap on principle; treat it as unverified until checked in GA4
// DebugView (Admin > DebugView) against a real logged-in session.
//
// Uses gtag('set', {user_id: ...}) -- the object form -- rather than
// gtag('set', 'user_id', ...): gtag's 'set' command is documented as
// gtag('set', TARGET, PARAMS) for a 3-arg call, so a 3-arg call with a string
// second arg reads as "set target 'user_id'" with the value in the wrong
// position, not "set the user_id param". The 2-arg object form is the
// unambiguous documented shape for setting default params outside a config
// call: https://developers.google.com/tag-platform/gtagjs/reference#set
//
// window.gtag is defined synchronously by gatsby-plugin-google-gtag's SSR-rendered
// head script (present before hydration), so no readiness race is expected here —
// same defensive check already used by sendEventsToGTag in components/Analytics.js.
// gtag('set', ...) mutates gtag's internal state immediately and synchronously, so
// once this resolves, every later event (including the batched, 5s-delayed
// Purchase event in checkout.js) carries it. UNRESOLVED RISK: the SSR plugin's own
// onRouteUpdate page_view is scheduled via 2 requestAnimationFrame calls + a
// setTimeout (a macrotask, ~32ms+), while this function's await is a microtask
// that should normally drain first -- but crypto.subtle.digest can hand off to a
// separate thread, and we have not measured actual ordering under load. The
// very first page_view after login could still go out without user_id. Purchase
// is unaffected (the 5s batch delay is far larger than either), but if GA4 needs
// user_id on a session's very first event to stitch it, first-page-view-after-
// login is the case to check in DebugView.
const setGtagUserId = async (session) => {
  if (IS_DEVELOPMENT) return;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  if (!session?.isLoggedIn) return;
  const userId = await getGtagUserId(session);
  if (!userId) return;
  window.gtag("set", { user_id: userId });
};

const App = (props) => {
  const { children } = props;
  log.debug("App::render() props:", props);

  // Seed the session SYNCHRONOUSLY from localStorage so the very first client
  // render already knows whether the user is logged in. Previously this started
  // as {} and hydrated one render later in a useEffect, leaving a window where
  // sessionContext.isLoggedIn === undefined. Layout(requireLoggedIn) treats that
  // as logged-out — it swaps {children} for <LoadingPage> and navigates to
  // /login — which UNMOUNTS the page subtree and tears down any in-progress
  // Stripe Elements (the intermittent /subscriptions add-card "Could not find
  // Stripe expiry field" flake). getSessionFromStorage returns {} during SSR.
  const [ sessionContext, setSessionContextState ] = useState(getSessionFromStorage);
  const setSessionContext = (newSessionContext) => {
    localStorage.setItem("sessionContext", JSON.stringify(newSessionContext));
    setSessionContextState(newSessionContext);

    // Keep the stage session cookie in step with the session: media.* audio
    // authenticates with the cookie, not with localStorage (see
    // setSessionCookie). Logout arrives here as {} -- clear the cookie too, so
    // the next anonymous media request does not ride a stale token.
    const sessionId = newSessionContext?.session?.session_id;
    if (sessionId) {
      setSessionCookie(sessionId);
    } else if (Object.keys(newSessionContext).length === 0) {
      clearSessionCookie();
    }

    // PostHog + GA4: identify on login, reset/clear on logout.
    identifyPostHogUser(newSessionContext);
    setGtagUserId(newSessionContext);
    if (
      !IS_DEVELOPMENT &&
      !newSessionContext?.isLoggedIn &&
      Object.keys(newSessionContext).length === 0
    ) {
      posthog.reset();
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        // null, not undefined -- GA4's documented convention for clearing
        // user_id, and safer against a merge implementation that might drop
        // an undefined value instead of overwriting with it.
        window.gtag("set", { user_id: null });
      }
    }
  };

  // Identify the already-seeded user to PostHog once on mount. We deliberately do
  // NOT call setSessionContext here — the state is already seeded synchronously
  // above, and re-setting it would churn the context identity on every page load,
  // re-rendering (and remounting) the whole tree, which is what tore down the
  // add-card Stripe Elements. (AdCritter init previously lived here; it is
  // currently commented out.)
  useEffect(() => {
    identifyPostHogUser(sessionContext);
    setGtagUserId(sessionContext);
    // A session restored from localStorage may have outlived its login-time
    // cookie (or never had one -- integration tests inject the session). Re-mirror
    // so the logged-in UI and the cookie media.* checks agree.
    setSessionCookie(sessionContext?.session?.session_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <SessionContext.Provider value={{ sessionContext, setSessionContext }}>
        <QueryClientProvider client={queryClient}>
          <LogRocketLazyLoader />
          <OpenReplayLazyLoader />
          <SupportChatProvider>{ children }</SupportChatProvider>
        </QueryClientProvider>
      </SessionContext.Provider>
      <div className="App">
        {/* <TawkMessengerReact
            propertyId="64730f81ad80445890ef84a0"
            widgetId="1h1gn94mi"/> */}
      </div>
    </>
  );
};

export default App;
